import React from 'react';
import { cn } from '@/lib/utils';
import EmptyState from './EmptyState';
import { Activity } from 'lucide-react';

const DOT_TONE = {
  success: 'bg-success ring-success/20',
  warning: 'bg-warning ring-warning/20',
  destructive: 'bg-destructive ring-destructive-text/20',
  primary: 'bg-primary ring-primary/20',
  muted: 'bg-muted-foreground ring-muted',
};

/**
 * Vertical activity timeline. `items`: [{ id, title, meta, timestamp, tone }]
 * Renders a real empty state when items is empty -- never placeholder rows.
 */
const Timeline = ({ items = [], renderTimestamp, emptyProps, className }) => {
  if (!items.length) {
    return (
      <EmptyState
        icon={Activity}
        title="No recent activity"
        description="Actions will appear here as they happen."
        compact
        {...emptyProps}
      />
    );
  }

  return (
    <ol className={cn('relative space-y-0', className)}>
      {items.map((item, idx) => (
        <li key={item.id ?? idx} className="relative flex gap-3 pb-5 last:pb-0">
          {idx !== items.length - 1 && (
            <span className="absolute left-[5px] top-3 h-[calc(100%-4px)] w-px bg-border" />
          )}
          <span
            className={cn(
              'relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full ring-4',
              DOT_TONE[item.tone] || DOT_TONE.muted
            )}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm text-foreground">{item.title}</p>
              <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground">
                {renderTimestamp ? renderTimestamp(item.timestamp) : item.timestamp}
              </span>
            </div>
            {item.meta && <p className="mt-0.5 text-xs text-muted-foreground">{item.meta}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default Timeline;
